import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteHighlight, fetchUserHighlights } from '../store/highlightSlice';
import Sidebar from './Sidebar';
import { Link } from 'react-router-dom';
import OpenModalButton from "../components/OpenModalButton";
import LoginForm from "../components/LoginForm";
import SignupForm from "../components/SignupForm";
import { FiSearch } from 'react-icons/fi'; // Import search icon

const HighlightsPage = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.session?.user);
  const { userHighlights = [], loading, error } = useSelector((state) => state.highlight || {});

  // 🔍 Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightPendingDelete, setHighlightPendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserHighlights());
    }
  }, [dispatch, user]);

  // 🔍 Filter highlights based on search query
  const filteredHighlights = userHighlights.filter(highlight =>
    highlight.book.toLowerCase().includes(searchQuery.toLowerCase()) ||
    highlight.chapter.toString().includes(searchQuery) ||
    (highlight.verse_id % 1000).toString().includes(searchQuery) ||
    highlight.selected_text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteHighlight = async () => {
    if (!highlightPendingDelete) return;

    try {
      setDeletingId(highlightPendingDelete.id);
      await dispatch(deleteHighlight(highlightPendingDelete.id)).unwrap();
      setHighlightPendingDelete(null);
    } catch (deleteError) {
      console.error('Failed to delete highlight:', deleteError);
    } finally {
      setDeletingId(null);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6 text-center">My Highlights</h1>
        <div className="bg-red-100 p-4 rounded-lg text-center">
          <p>
            Please{" "}
            <OpenModalButton
              buttonText={
                <span className="text-blue-700 hover:text-blue-500">
                  log in
                </span>
              }
              modalComponent={<LoginForm />}
            />{" "}
            or{" "}
            <OpenModalButton
              buttonText={
                <span className="text-blue-700 hover:text-blue-500">
                  sign up
                </span>
              }
              modalComponent={<SignupForm />}
            />{" "}
            to view your highlights.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 p-6 lg:p-8 ml-[80px]">
        <h1 className="text-4xl font-bold text-gray-900 text-center mb-8">My Highlights</h1>

        {/* 🔍 Search Bar */}
        <div className="relative max-w-lg mx-auto mb-6">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search highlights..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* Loading & Error Handling */}
        {loading && <p className="text-center text-blue-500 animate-pulse">Loading highlights...</p>}
        {error && <p className="text-center text-red-500">{error}</p>}

        {/* Empty State */}
        {!loading && !error && userHighlights.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-600 text-lg">You have no highlights yet. Start highlighting verses!</p>
          </div>
        )}

        {/* Search No Results State */}
        {!loading && !error && userHighlights.length > 0 && filteredHighlights.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-600 text-lg">No matching highlights found.</p>
          </div>
        )}

        {/* Highlights Grid */}
        {filteredHighlights.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredHighlights.map((highlight) => (
              <div
                key={highlight.id}
                className="relative bg-white p-5 rounded-lg shadow-md hover:shadow-xl transition-transform transform hover:-translate-y-1 border border-gray-200"
              >
                <button
                  type="button"
                  onClick={() => setHighlightPendingDelete(highlight)}
                  className="absolute right-4 top-4 rounded-md border border-red-200 px-3 py-1 text-sm text-red-700 hover:bg-red-50"
                >
                  Delete
                </button>

                <Link
                  to={`/bible/${highlight.version}/${highlight.book}/${highlight.chapter}`}
                  className="group block"
                >
                  <h3 className="pr-20 text-xl font-semibold text-gray-800 group-hover:text-blue-600">
                    {highlight.version} {highlight.book} {highlight.chapter}:{highlight.verse_id % 1000}
                  </h3>
                  <p className="mt-2 text-gray-700 italic truncate">{highlight.selected_text}</p>

                  <div className="mt-4 text-gray-500 text-sm flex justify-between">
                    <span>{highlight.book} {highlight.chapter}:{highlight.verse_id % 1000}</span>
                    <span>{new Date(highlight.created_at).toLocaleDateString()}</span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {highlightPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <p className="text-lg font-semibold text-gray-900">Delete this highlight?</p>
            <p className="mt-2 text-sm text-gray-600">
              This will permanently remove the saved highlight.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setHighlightPendingDelete(null)}
                className="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-800 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteHighlight}
                disabled={deletingId === highlightPendingDelete.id}
                className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId === highlightPendingDelete.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HighlightsPage;
